import type { JSX as LocalJSX } from 'blip-ds/loader';
import { DetailedHTMLProps, HTMLAttributes } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.scss';
import './styles.scss';

type StencilToReactElements<T = LocalJSX.IntrinsicElements> = {
  [P in keyof T]?: T[P] &
    Omit<DetailedHTMLProps<HTMLAttributes<T[P]>, T[P]>, 'className'> & {
      class?: string;
    };
};

type StencilToReactRef<T = HTMLElementTagNameMap> = {
  [P in keyof T]: {
    ref?: DetailedHTMLProps<HTMLAttributes<T[P]>, T[P]>['ref'];
  };
};

type StencilToReact<T = LocalJSX.IntrinsicElements, U = HTMLElementTagNameMap> = StencilToReactElements<T> &
  StencilToReactRef<U>;

declare global {
  export namespace JSX {
    interface IntrinsicElements extends StencilToReact {}
  }
}

// blip-ds ships its own lazy-loading Stencil module graph. It must be loaded as
// plain, unbundled ES modules (copied verbatim, see vite.config.ts) so every
// dynamically-imported component shares the same runtime instance — bundling it
// through Rollup instead duplicates Stencil's internal host-element state.
// (Non-literal specifiers below so neither TS nor Vite try to resolve/bundle them.)
const polyfillsUrl = '/blip-ds/polyfills/index.js';
const loaderUrl = '/blip-ds/loader.js';

const [{ applyPolyfills }, { defineCustomElements }] = await Promise.all([
  import(/* @vite-ignore */ polyfillsUrl),
  import(/* @vite-ignore */ loaderUrl),
]);
await applyPolyfills();
defineCustomElements(globalThis);

createRoot(document.getElementById('root')!).render(<App />);
